import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Center,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Select,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { createSearch, listEligibleClientNeeds } from "../../api/searchApi";

const MAX_RFP_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

const SourcePicker = ({ buId, onCreated }) => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [needs, setNeeds] = useState([]);
  const [isLoadingNeeds, setIsLoadingNeeds] = useState(false);
  const [selectedNeedId, setSelectedNeedId] = useState("");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!buId) return undefined;
    let cancelled = false;
    setIsLoadingNeeds(true);
    (async () => {
      try {
        const data = await listEligibleClientNeeds({ limit: 50 }, { buId });
        if (cancelled) return;
        setNeeds(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load eligible client needs", err);
        toast({
          title: t("asapProfile.create.errors.needsLoadFailed"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) setIsLoadingNeeds(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buId, toast, t]);

  const submit = async (payload) => {
    setIsSubmitting(true);
    try {
      const created = await createSearch(payload, { buId });
      toast({
        title: t("asapProfile.create.successToast"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onCreated?.(created.id);
      setSelectedNeedId("");
      setFile(null);
    } catch (err) {
      console.error("Failed to create search", err);
      const detail = err?.response?.data?.detail;
      // The search-budget guard returns a 402 with a structured detail
      // object — surface a dedicated toast so the user knows the issue
      // is the monthly cap and not a generic upload failure.
      const code = typeof detail === "object" && detail !== null ? detail.code : null;
      if (code === "PDL_SEARCH_BUDGET_EXCEEDED") {
        toast({
          title: t("asapProfile.create.errors.searchBudgetExceededTitle"),
          description: t("asapProfile.create.errors.searchBudgetExceededBody", {
            used: detail?.currentMonthSearchesUsed ?? 0,
            budget: detail?.monthlySearchBudget ?? 0,
          }),
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
        return;
      }
      const lookup = {
        FILE_TOO_LARGE: t("asapProfile.create.errors.fileTooLarge"),
        UNSUPPORTED_FILE_TYPE: t("asapProfile.create.errors.unsupportedType"),
        CLIENT_NEED_NOT_FOUND: t("asapProfile.create.errors.needNotFound"),
        EXTRACTION_FAILED: t("asapProfile.create.errors.extractFailed"),
      };
      toast({
        title: lookup[detail] || t("asapProfile.create.errors.createFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNeedChange = (e) => {
    const value = e.target.value;
    setSelectedNeedId(value);
    if (!value) return;
    submit({ clientNeedId: value });
  };

  const validateAndUpload = (candidate) => {
    if (!candidate) return;
    const lower = (candidate.name || "").toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
      toast({
        title: t("asapProfile.create.errors.unsupportedType"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (candidate.size > MAX_RFP_BYTES) {
      toast({
        title: t("asapProfile.create.errors.fileTooLarge"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setFile(candidate);
    submit({ file: candidate });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const list = e.dataTransfer?.files;
    if (list && list.length > 0) validateAndUpload(list[0]);
  };

  const handleFilesSelected = (e) => {
    const list = e.target.files;
    if (list && list.length > 0) validateAndUpload(list[0]);
  };

  return (
    <VStack
      align="stretch"
      spacing={3}
      data-testid="source-picker"
      opacity={isSubmitting ? 0.7 : 1}
      pointerEvents={isSubmitting ? "none" : "auto"}
    >
      <FormControl isDisabled={isSubmitting || isLoadingNeeds}>
        <FormLabel sx={{ marginBottom: "4px !important" }}>
          {t("asapProfile.create.tabs.pilot")}
        </FormLabel>
        <Select
          size="sm"
          value={selectedNeedId}
          onChange={handleNeedChange}
          placeholder={t("asapProfile.create.pilot.searchPlaceholder")}
          data-testid="source-pilot-select"
          height="32px"
          lineHeight="32px"
          paddingTop="0"
          paddingBottom="0"
          sx={{
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
            lineHeight: "32px !important",
            height: "32px !important",
          }}
        >
          {needs.map((need) => (
            <option key={need.id} value={need.id}>
              {need.title}
              {need.clientName ? ` — ${need.clientName}` : ""}
            </option>
          ))}
        </Select>
      </FormControl>

      <HStack spacing={3} align="center">
        <Box flex="1" h="1px" bg="gray.200" />
        <Text
          fontSize="xs"
          color="gray.500"
          sx={{ marginBottom: "0 !important" }}
        >
          {t("asapProfile.create.orLabel")}
        </Text>
        <Box flex="1" h="1px" bg="gray.200" />
      </HStack>

      <Box
        as="button"
        type="button"
        role="button"
        aria-label={t("asapProfile.create.upload.dropText")}
        aria-disabled={isSubmitting || undefined}
        border="2px dashed"
        borderColor={dragActive ? "blue.400" : "gray.300"}
        borderRadius="md"
        p={4}
        textAlign="center"
        width="100%"
        bg={dragActive ? "blue.50" : "transparent"}
        cursor={isSubmitting ? "not-allowed" : "pointer"}
        onClick={() => !isSubmitting && fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (isSubmitting) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="source-dropzone"
      >
        {isSubmitting ? (
          <Center py={2}>
            <HStack>
              <Spinner size="sm" />
              <Text
                fontSize="sm"
                color="gray.600"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("asapProfile.create.creating")}
              </Text>
            </HStack>
          </Center>
        ) : (
          <VStack spacing={1}>
            <Icon as={FaCloudUploadAlt} boxSize={6} color="gray.400" />
            <Text
              fontSize="sm"
              fontWeight="medium"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("asapProfile.create.upload.dropText")}
            </Text>
            <Text
              fontSize="xs"
              color="gray.500"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("asapProfile.create.upload.acceptedFormats")}
            </Text>
          </VStack>
        )}
        <Input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          onChange={handleFilesSelected}
          display="none"
          aria-label={t("asapProfile.create.upload.dropText")}
          data-testid="source-file-input"
        />
      </Box>

      {file && !isSubmitting && (
        <HStack spacing={2}>
          <Tag size="md" variant="subtle" colorScheme="teal" borderRadius="full">
            <TagLabel>{file.name}</TagLabel>
            <TagCloseButton onClick={() => setFile(null)} />
          </Tag>
          <Text
            fontSize="xs"
            color="gray.500"
            sx={{ marginBottom: "0 !important" }}
          >
            {formatSize(file.size)}
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export default SourcePicker;
